class TabGroup extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.panels = [];
    this.activeTabIndex = 0;
    this.classList.add('initiated_tab');
  }

  connectedCallback() {
    // Simple initialization - no complex waiting
    this.setupTabs();
    this.setupEventListeners();
    this.activateDefaultTab();
    this.attachResizeListener();
    this.updateHeaderWidths();
  }

  setupTabs() {
    try {
      this.tabs = Array.from(this.querySelectorAll('tab-header'));
      this.panels = Array.from(this.querySelectorAll('tab-panel'));
    } catch (error) {
      console.error('Error setting up tabs:', error);
    }
  }

  setupEventListeners() {
    try {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          this.activateTab(index);
        });
      });
    } catch (error) {
      console.error('Error setting up event listeners:', error);
    }
  }

  activateDefaultTab() {
    if (this.tabs.length === 0) return;

    // Find tab with 'open' attribute
    const openTabIndex = this.tabs.findIndex((tab) => tab.hasAttribute('open'));

    if (openTabIndex !== -1) {
      this.activeTabIndex = openTabIndex;
      this.showTab(openTabIndex);
    } else {
      // Default to first tab
      this.activeTabIndex = 0;
      this.showTab(0);
    }

    // Hide all other panels
    this.panels.forEach((panel, index) => {
      if (index !== this.activeTabIndex) {
        panel.classList.add('hidden');
        panel.classList.remove('block');
      }
    });
  }

  activateTab(index) {
    if (index < 0 || index >= this.tabs.length) return;
    if (this.activeTabIndex === index) return; // Already active

    // Hide current tab
    this.hideTab(this.activeTabIndex);

    // Show new tab
    this.showTab(index);

    this.activeTabIndex = index;
  }

  showTab(index) {
    if (index < 0 || index >= this.tabs.length) return;

    const tab = this.tabs[index];
    const panel = this.panels[index];

    if (tab) {
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      tab.removeAttribute('tabindex');
    }

    if (panel) {
      panel.classList.remove('hidden');
      panel.classList.add('block');

      // Update any Swiper instances inside the panel (if any exist)
      requestAnimationFrame(() => {
        try {
          const swiperContainers = panel.querySelectorAll('swiper-container');
          if (swiperContainers.length > 0) {
            swiperContainers.forEach((container) => {
              if (
                container &&
                container.swiper &&
                typeof container.swiper.update === 'function'
              ) {
                container.swiper.update();
                // Force scrollbar update
                if (
                  container.swiper.scrollbar &&
                  typeof container.swiper.scrollbar.updateSize === 'function'
                ) {
                  container.swiper.scrollbar.updateSize();
                }
              }
            });
          }
        } catch (error) {
          // Silently ignore errors - tabs might not contain sliders
        }
      });
    }
  }

  hideTab(index) {
    if (index < 0 || index >= this.tabs.length) return;

    const tab = this.tabs[index];
    const panel = this.panels[index];

    if (tab) {
      tab.classList.remove('active');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    }

    if (panel) {
      panel.classList.add('hidden');
      panel.classList.remove('block');
    }
  }

  disconnectedCallback() {
    this.detachResizeListener();
  }

  updateHeaderWidths() {
    // Reset widths first so natural size can be measured
    const tabs = this.querySelectorAll('tab-header');
    tabs.forEach((header) => {
    });

    // Collect images if they exist
    const images = this.querySelectorAll(".tab_header img");
    const imagePromises = Array.from(images).map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            })
    );

    Promise.all(imagePromises).then(() => {
      const lastHeader = tabs[tabs.length - 1];
      
      tabs.forEach((header) => {
        let extra = 0;
        if (header === lastHeader && header.closest("[data-show-extension='true']")) extra = 10;
        header.style.setProperty("--header_extra_width", `${extra}px`);

        let headerContentWidth = 0;
        if (header.querySelector("img")) {
          headerContentWidth = header.querySelector("img").offsetWidth;
        } else {
          headerContentWidth = header.querySelector("p").offsetWidth;
        }

        if (header.classList.contains("active")) {
          header.style.setProperty("--header_width", `${headerContentWidth}px`);
        } else {
          header.style.setProperty("--header_width", `${headerContentWidth + 10}px`);
        }
        header.classList.add("header_adjusted");
      });
    });
  }

  attachResizeListener() {
    this._resizeHandler = this._debounce(() => this.updateHeaderWidths(), 150);
    window.addEventListener("resize", this._resizeHandler);
  }

  detachResizeListener() {
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
      this._resizeHandler = null;
    }
  }

  _debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

class TabHeader extends HTMLElement {
  constructor() {
    super();
    this.setAttribute('role', 'tab');
    this.setAttribute('tabindex', '-1');
    this.setAttribute('aria-selected', 'false');
  }

  connectedCallback() {
    // Add basic styling classes if not present
    if (!this.classList.contains('cursor-pointer')) {
      this.classList.add('cursor-pointer');
    }
  }
}

class TabPanel extends HTMLElement {
  constructor() {
    super();
    this.setAttribute('role', 'tabpanel');
    // Don't add hidden by default - let parent handle visibility
  }

  connectedCallback() {
    // Let the parent TabGroup handle visibility
  }
}

// Register custom elements
customElements.define('tab-group', TabGroup);
customElements.define('tab-header', TabHeader);
customElements.define('tab-panel', TabPanel);
